import { yupResolver } from "@hookform/resolvers/yup";
import React, { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import AppInputNew from "../../components/Core/AppInput/AppInputNew";
import { AppSelectController } from "../../components/Core/AppSelect/AppSelect";
import Button from "../../components/Core/Button/Button";
import { FormCard } from "../../components/Core/FormCard";
import TextAreaNew from "../../components/Core/TextArea/TextAreaNew";
import FloatingButtonPanel from "../../components/Shared/FloatingButtonPanel/FloatingButtonPanel";
import useUnsavedChangesInFormChecker from "../../hooks/useUnsavedChangesChecker";
import Address from "../../models/Address";
import Consultant, { UpdateConsultantRequest } from "../../models/Consultant";
import { Pool } from "../../models/Organisation";
import { useUpdateCandidate } from "../../networking/candidate.hooks";
import { useOrganisation } from "../../networking/organisation.hooks";
import { useSubsidiaries } from "../../networking/subsidiary.hooks";
import {
	useAvailablePools,
	ORGANISATION_DIRECTLY_VALUE,
} from "../../components/Shared/SubsidiarySelect/SubsidiarySelect";
import phoneNumberSchema from "../../schemas/phoneNumberSchema";
import postalCodeSchema from "../../schemas/postalCodeSchema";
import socialSecurityNumberSchema from "../../schemas/socialSecurityNumberSchema";

type CandidateInputs = {
	firstName: string;
	lastName: string;
	email: string;
	profile: {
		address: Address;
		socialSecurityNumber?: string;
		phoneNumber?: string;
		presentation?: string;
		internalNotes?: string;
		pool?: string;
		subsidiaryId?: string | null;
		title?: string;
		icePhone?: string;
		iceName?: string;
		recruitment: {
			interviewNotes?: string;
		};
	};
};

const CandidateInputsValidationSchema = yup.object().shape({
	email: yup.string().email("EMAIL_NOT_VALID").required("FIELD_IS_REQUIRED"),
	firstName: yup.string().required("FIELD_IS_REQUIRED"),
	lastName: yup.string().required("FIELD_IS_REQUIRED"),
	profile: yup.object().shape({
		phoneNumber: phoneNumberSchema.required("FIELD_IS_REQUIRED"),
		socialSecurityNumber: socialSecurityNumberSchema.required("FIELD_IS_REQUIRED"),
		address: yup.object().shape({
			postalCode: postalCodeSchema,
		}),
	}),
});

const RecruitmentProfileTab: React.FC<{ candidate: Consultant }> = ({ candidate }) => {
	const { t } = useTranslation();

	const { data: organisation } = useOrganisation();

	const { mutate: updateCandidate, isLoading } = useUpdateCandidate();

	const { subsidiaries } = useSubsidiaries();

	const { register, handleSubmit, formState, reset, control } = useForm<CandidateInputs>({
		defaultValues: useMemo(() => {
			return { ...candidate };
		}, [candidate]),
		mode: "onBlur",
		resolver: yupResolver(CandidateInputsValidationSchema),
	});

	const availablePools = useAvailablePools(
		control,
		"profile.subsidiaryId",
		subsidiaries,
		organisation?.pools,
		candidate.profile?.pool
	);

	// "Inget dotterbolag" uses a sentinel value (Radix forbids an empty
	// SelectItem value); on submit it maps to an undefined subsidiaryId.
	const subsidiaryOptions: { key: string; name: string }[] = [
		{ key: ORGANISATION_DIRECTLY_VALUE, name: t("NO_SUBSIDIARY") },
		...subsidiaries.map((s) => ({ key: s.id, name: s.name })),
	];

	const onSubmit = handleSubmit(async (e) => {
		const updateCandidateProfile: UpdateConsultantRequest = {
			firstName: e.firstName,
			lastName: e.lastName,
			email: e.email,
			profile: {
				address: {
					address1: e.profile.address.address1,
					address2: e.profile.address.address2,
					postalCode: e.profile.address.postalCode,
					city: e.profile.address.city,
				},
				socialSecurityNumber: e.profile.socialSecurityNumber,
				phoneNumber: e.profile.phoneNumber,
				// presentation: e.profile.presentation,
				internalNotes: e.profile.internalNotes,
				pool: e.profile.pool,
				// null disconnects (belong to the organisation directly)
				subsidiaryId:
					e.profile.subsidiaryId && e.profile.subsidiaryId !== ORGANISATION_DIRECTLY_VALUE
						? e.profile.subsidiaryId
						: null,
				iceName: e.profile.iceName,
				icePhone: e.profile.icePhone,
				recruitment: {
					interviewNotes: e.profile.recruitment?.interviewNotes,
				},
			},
		};

		if (candidate?.id) {
			await updateCandidate({ userId: candidate.id, update: updateCandidateProfile });
		}
	});

	useEffect(() => {
		reset({ ...candidate });
	}, [candidate, reset]);

	useUnsavedChangesInFormChecker(formState);

	return (
		<form onSubmit={onSubmit}>
			<div className="max-w-[1400px] pr-4 py-6">
				<div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-2">
					{/* Left column — identity, address & emergency contact */}
					<div className="flex flex-col gap-5">
						<FormCard title="BASIC_INFO">
							<div className="grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2">
								<AppInputNew
									{...register("firstName")}
									label="FIRST_NAME"
									icon="name"
									placeholder="FIRST_NAME"
									errors={formState.errors?.firstName?.message}
									mandatory
								/>
								<AppInputNew
									{...register("lastName")}
									label="LAST_NAME"
									icon="name"
									placeholder="LAST_NAME"
									errors={formState.errors?.lastName?.message}
									mandatory
								/>
								{/* Full width — addresses are long, and it pairs phone with the
								    personal number on the next row. */}
								<AppInputNew
									{...register("email")}
									containerClassName="md:col-span-2"
									label="EMAIL"
									icon="email"
									placeholder="EMAIL"
									errors={formState.errors?.email?.message}
									mandatory
								/>
								<AppInputNew
									{...register("profile.phoneNumber")}
									label="PHONE"
									icon="phone"
									placeholder="PHONE_PLACEHOLDER"
									errors={formState.errors?.profile?.phoneNumber?.message}
									mandatory
								/>
								<AppInputNew
									{...register("profile.socialSecurityNumber")}
									label="PERSONAL_NUMBER"
									placeholder="ÅÅÅÅMMDD-XXXX"
									errors={formState.errors?.profile?.socialSecurityNumber?.message}
									mandatory
								/>
								{/* Organisation placement, kept paired on its own row at the
								    bottom of the card (leaving the personal number orphaned). */}
								<div className="grid grid-cols-1 gap-x-6 gap-y-4 md:col-span-2 md:grid-cols-2">
									{availablePools.length > 0 && (
										<AppSelectController<Pool>
											control={control}
											label="POOL"
											name="profile.pool"
											options={availablePools}
											getOptionLabel={(option) => option.name}
											getOptionValue={(o) => o.key as string}
											errors={formState.errors?.profile?.pool?.message}
											placeholder="SELECT_POOL"
											margin="0"
										/>
									)}
									{subsidiaries.length > 0 && (
										<AppSelectController<{ key: string; name: string }>
											control={control}
											label="BELONGS_TO_SUBSIDIARY"
											name="profile.subsidiaryId"
											options={subsidiaryOptions}
											getOptionLabel={(o) => o.name}
											getOptionValue={(o) => o.key}
											placeholder="NO_SUBSIDIARY"
											margin="0"
										/>
									)}
								</div>
							</div>
						</FormCard>

						<FormCard title="ADDRESS">
							<div className="flex flex-col gap-4">
								<AppInputNew
									{...register("profile.address.address1")}
									label="STREET_ADDRESS"
									placeholder="ADDRESS_PLACEHOLDER"
								/>
								<AppInputNew
									{...register("profile.address.address2")}
									placeholder="ADDRESS_PLACEHOLDER_2"
								/>
								<div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
									<AppInputNew
										{...register("profile.address.postalCode")}
										label="POSTAL_CODE"
										placeholder="POSTAL_CODE_PLACEHOLDER"
										errors={formState.errors?.profile?.address?.postalCode?.message}
									/>
									<AppInputNew
										{...register("profile.address.city")}
										label="CITY"
										placeholder="CITY_PLACEHOLDER"
									/>
								</div>
							</div>
						</FormCard>

						<FormCard title="EMERGENCY_CONTACT">
							<div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
								<AppInputNew
									{...register("profile.iceName")}
									label="ice.iceName"
									placeholder="ice.iceNamePlaceholder"
								/>
								<AppInputNew
									{...register("profile.icePhone")}
									label="ice.icePhone"
									placeholder="ice.icePhonePlaceholder"
								/>
							</div>
						</FormCard>
					</div>

					{/* Right column — recruitment notes */}
					<div className="flex flex-col gap-5">
						<FormCard title="INTERVIEW_NOTES">
							<TextAreaNew
								{...register("profile.recruitment.interviewNotes")}
								height="250px"
								placeholder="INTERVIEW_NOTES_PLACEHOLDER"
							/>
						</FormCard>

						<FormCard title="recruitment.otherNotes">
							<TextAreaNew
								{...register("profile.internalNotes")}
								height="250px"
								placeholder="recruitment.otherNotes"
							/>
						</FormCard>
					</div>
				</div>
			</div>
			<FloatingButtonPanel>
				<Button width="10rem" disabled={isLoading} loading={isLoading}>
					{t("SAVE_CHANGES")}
				</Button>
			</FloatingButtonPanel>
		</form>
	);
};

export default RecruitmentProfileTab;
